"use client";

import { useMemo, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  CheckboxRow,
  SegmentedControl,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* Builds a Personal Emergency Evacuation Plan (PEEP) — the bespoke escape plan a
   Responsible Person must put in place for an individual who cannot evacuate
   unaided, under the Regulatory Reform (Fire Safety) Order 2005 (and reflecting
   Equality Act duties). Captures the person, their needs and the escape strategy
   (independent / buddy assistance / evacuation chair / refuge), then prints the plan.

   Compliance (YMYL): a planning aid that drafts a PEEP. The Responsible Person is
   accountable for a workable plan, must practise it, and must share PEEPs with the
   building's responsible person in multi-occupied premises. Not a substitute for a
   competent assessment of complex evacuation needs. */

const NEEDS = [
  { id: "stairs", label: "Cannot use stairs unaided" },
  { id: "mobility", label: "Limited mobility / slow on stairs" },
  { id: "vision", label: "Vision impairment" },
  { id: "hearing", label: "Hearing impairment — may not hear the alarm" },
  { id: "cognitive", label: "Cognitive / learning difficulty" },
  { id: "temporary", label: "Temporary (e.g. injury, pregnancy)" },
];

const STRATEGIES = [
  { value: "independent", label: "Evacuates independently (may need more time)" },
  { value: "buddy", label: "Assisted down stairs by trained buddies" },
  { value: "evac-chair", label: "Evacuation chair (trained operators)" },
  { value: "refuge", label: "Refuge — await assisted rescue" },
  { value: "horizontal", label: "Horizontal evacuation to a fire compartment" },
];

export function PeepBuilder() {
  const [name, setName] = useState("");
  const [role, setRole] = useState("employee");
  const [building, setBuilding] = useState("");
  const [location, setLocation] = useState("");
  const [needs, setNeeds] = useState<Record<string, boolean>>({});
  const [alert, setAlert] = useState("standard");
  const [strategy, setStrategy] = useState("");
  const [assistants, setAssistants] = useState("");
  const [equipment, setEquipment] = useState("");
  const [refuge, setRefuge] = useState("");
  const [assembly, setAssembly] = useState("");
  const [rp, setRp] = useState("");

  const toggleNeed = (id: string, v: boolean) => setNeeds((s) => ({ ...s, [id]: v }));
  const needList = NEEDS.filter((n) => needs[n.id]).map((n) => n.label);

  const ready = name && strategy;
  const gaps = useMemo(() => {
    const g: string[] = [];
    if ((strategy === "buddy" || strategy === "evac-chair") && !assistants) g.push("Name the trained assistants / buddies (an evacuation chair usually needs two).");
    if (strategy === "evac-chair" && !equipment) g.push("Record the evacuation-chair location.");
    if (strategy === "refuge" && !refuge) g.push("Record the refuge location and its two-way communication.");
    if (needs.hearing && alert === "standard") g.push("A standard alarm may not be heard — add a visual/vibrating alert or a buddy to alert them.");
    if (!rp) g.push("Name the Responsible Person and share the PEEP with the building's responsible person.");
    return g;
  }, [strategy, assistants, equipment, refuge, needs, alert, rp]);

  if (ready && gaps.length === 0) trackToolResult("peep-builder", { strategy, needs: needList.length });

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Person's name"><TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. J. Smith" /></Field>
        <Field label="They are a…">
          <SelectInput value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="employee">Employee</option>
            <option value="resident">Resident</option>
            <option value="regular-visitor">Regular visitor</option>
            <option value="student">Student / pupil</option>
          </SelectInput>
        </Field>
        <Field label="Building"><TextInput value={building} onChange={(e) => setBuilding(e.target.value)} placeholder="e.g. Maple House" /></Field>
        <Field label="Floor / usual location"><TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. 3rd floor, desk 12" /></Field>
      </div>

      <fieldset className="rounded-2xl border border-line bg-white p-5 shadow-soft">
        <legend className="px-2 font-display text-base font-bold text-ink">Why a PEEP is needed</legend>
        <div className="mt-3 space-y-2.5">
          {NEEDS.map((n) => <CheckboxRow key={n.id} checked={!!needs[n.id]} onChange={(v) => toggleNeed(n.id, v)}>{n.label}</CheckboxRow>)}
        </div>
      </fieldset>

      <Field label="How they'll be alerted to a fire">
        <SegmentedControl
          legend="Alerting method"
          name="alert"
          value={alert}
          onChange={setAlert}
          options={[
            { value: "standard", label: "Standard alarm", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
            { value: "visual", label: "Visual / vibrating", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
            { value: "buddy", label: "Alerted by buddy", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
          ]}
        />
      </Field>

      <Field label="Escape strategy" hint="How this person gets to a place of total safety.">
        <SelectInput value={strategy} onChange={(e) => setStrategy(e.target.value)}>
          <option value="">Select…</option>
          {STRATEGIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </SelectInput>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Assistants / buddies (named, trained)"><TextInput value={assistants} onChange={(e) => setAssistants(e.target.value)} placeholder="e.g. A. Jones, R. Patel" /></Field>
        <Field label="Equipment & location"><TextInput value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder="e.g. Evac chair — Level 3 stair core" /></Field>
        <Field label="Refuge point & communication"><TextInput value={refuge} onChange={(e) => setRefuge(e.target.value)} placeholder="e.g. Level 3 refuge, intercom to reception" /></Field>
        <Field label="Final assembly point"><TextInput value={assembly} onChange={(e) => setAssembly(e.target.value)} placeholder="e.g. Front car park" /></Field>
        <Field label="Responsible Person"><TextInput value={rp} onChange={(e) => setRp(e.target.value)} placeholder="Who owns this PEEP" /></Field>
      </div>

      {ready && (
        <ResultBanner level={gaps.length ? "medium" : "low"} title={gaps.length ? `${gaps.length} thing${gaps.length > 1 ? "s" : ""} to complete` : "PEEP ready to print and practise"}>
          {gaps.length ? (
            <ul className="mt-1 list-disc space-y-1 pl-5">{gaps.map((g) => <li key={g}>{g}</li>)}</ul>
          ) : (
            <p>Print it, brief the person and their assistants, and <strong>practise it</strong> — a PEEP that has never been rehearsed is not a workable plan. Review it if the person's needs, role or location change.</p>
          )}
        </ResultBanner>
      )}

      {/* Printable PEEP record */}
      {ready && (
        <div className="rounded-2xl border border-line bg-white p-6 shadow-soft">
          <h3 className="font-display text-lg font-bold text-ink">Personal Emergency Evacuation Plan</h3>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Row k="Name" v={name} />
            <Row k="Role" v={role.replace("-", " ")} />
            <Row k="Building" v={building} />
            <Row k="Location" v={location} />
            <Row k="Needs" v={needList.join("; ")} full />
            <Row k="Alerted by" v={alert === "standard" ? "Standard fire alarm" : alert === "visual" ? "Visual / vibrating alert" : "Alerted by a buddy"} />
            <Row k="Escape strategy" v={STRATEGIES.find((s) => s.value === strategy)?.label ?? ""} />
            <Row k="Assistants" v={assistants} />
            <Row k="Equipment" v={equipment} />
            <Row k="Refuge & comms" v={refuge} />
            <Row k="Assembly point" v={assembly} />
            <Row k="Responsible Person" v={rp} />
          </dl>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="peep-builder" label="Print / save this PEEP" />
      </div>
      <PrintFooter toolName="Personal Emergency Evacuation Plan" />
    </div>
  );
}

function Row({ k, v, full }: { k: string; v: string; full?: boolean }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="text-[0.7rem] font-semibold uppercase tracking-wider text-muted">{k}</dt>
      <dd className="mt-0.5 text-body">{v || <span className="text-muted">—</span>}</dd>
    </div>
  );
}
